/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { Box, Flex, Radio, RadioGroup, Stack, IconButton, Tooltip, Text, useToast, Center } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import { DownloadIcon } from '@chakra-ui/icons';
import { chartBackgroundColor } from '../../Miscellaneous/plugins-chart';
import { formatCurrency, formatPercentage } from '../../../../utils/format';
import Chart from 'react-chartjs-2';
import Card from '../../../Card/Card';
import chartDataLabels from 'chartjs-plugin-datalabels';
import DataLoading from '../../../DataLoading';

const Q_DATASET = gql`
	query get($type: MDDataPolicyGrossPremiumEnumType!, $companyType: InsuranceInstitutionsType, $companyId: String, $range: MDDataDateRangeInput) {
		getDistributionChannelNumPolicyAndGrossPremiumByChannelDataset(type: $type, companyType: $companyType, companyId: $companyId, range: $range)
	}
`;

const mapping = {
	1: 'Brokers',
	2: 'Individual Agent',
	3: 'Corporate Agent',
	4: 'Bancassurance',
	5: 'Digital Sale',
	6: 'Sale Staff',
	7: 'Number of Insured',
	8: 'number',
	9: 'amount1',
};

function Graph({ companyType = null, companyId = null, range = null }) {
	const chartRef = useRef(null);
	const toast = useToast();
	const { t } = useTranslation();
	const [type, setType] = useState('Policy');
	const [getDataset, { data, error, loading }] = useLazyQuery(Q_DATASET, { fetchPolicy: 'no-cache' });

	useEffect(load, [companyType, companyId, range, type]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					})
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { type, companyType, companyId, range: range?.from && range?.to ? range : null };
		getDataset({ variables });
	}

	return (
		<Box className={loading ? 'disableFormClear' : ''}>
			<Card>
				<Flex justifyContent="center" alignItems="center" flexDirection="column" p={4}>
					<Text fontSize="lg" fontWeight="bold" color="gray.600" noOfLines={2} textAlign={'center'} pl={4} pr={4}>
						{t('Policy and Gross Premium by Channel')}
					</Text>
				</Flex>
				<DataLoading loading={loading} loadedData={data?.getDistributionChannelNumPolicyAndGrossPremiumByChannelDataset}>
					{({ loadedData }) => (
						<>
							<Flex justifyContent="center" alignItems="center" flexDirection="column">
								<RadioGroup onChange={setType} value={type}>
									<Stack direction="row">
										<Text fontSize="sm">{t('Show')}</Text>
										<Radio size="sm" value="Policy">
											{t('Policy')}
										</Radio>
										<Radio size="sm" value="GrossPremium">
											{t('Gross Premium')}
										</Radio>
									</Stack>
								</RadioGroup>
							</Flex>
							<Box width="100%" p={4} pt={0}>
								<Center>
									<Chart
										ref={chartRef}
										type="doughnut"
										width="260px"
										height="260px"
										plugins={[chartDataLabels, chartBackgroundColor]}
										options={{
											maintainAspectRatio: false,
											canvasBackgroundColor: {
												color: '#FFFFFF',
											},
											layout: {
												padding: {
													top: 32,
													right: 16,
													bottom: 16,
													left: 8,
												},
											},
											plugins: {
												title: { display: false },
												legend: {
													display: true,
													labels: {
														font: {
															family: 'Rajdhani, Hanuman, sans-serif, Moul',
															weight: 'normal',
															size: 12,
														},
														generateLabels(chart) {
															return chart.data.labels.map((el, i) => {
																return {
																	text: t(mapping[el]),
																	fillStyle: chart.data.datasets[0].backgroundColor[i],
																	strokeStyle: chart.data.datasets[0].borderColor[i],
																	lineWidth: chart.data.datasets[0].borderWidth,
																	pointStyle: 'rect',
																	hidden: !chart.getDataVisibility(i),
																	index: i,
																};
															});
														},
														usePointStyle: true,
														pointStyle: 'rect',
													},
													position: 'left',
												},
												datalabels: {
													display: true,
													color: 'white',
													font: {
														family: 'Rajdhani, Hanuman, sans-serif, Moul',
														weight: 'bold',
														size: 10,
													},
													formatter: function (value, context) {
														return formatPercentage(value);
													},
												},
												tooltip: {
													callbacks: {
														label: (toolTipItem) => {
															return t(mapping[toolTipItem?.label]);
														},
														footer: (toolTipItems) => {
															let percent = 0;
															let number = 0;
															toolTipItems?.forEach((el) => {
																percent = percent + parseFloat(el?.dataset?.data[el?.dataIndex] || 0);
																number = number + parseFloat(el?.dataset?.number[el?.dataIndex] || 0);
															});
															const titlePercent = t('Percentage') + ': ' + percent?.toFixed(2) + '%';
															const titlePolicy = t('Policy') + ': ' + number?.toFixed(0);
															const titleGrossPremium = t('Gross Premium') + ': ' + formatCurrency(number?.toFixed(2));
															return [titlePercent, type === 'Policy' ? titlePolicy : titleGrossPremium];
														},
													},
												},
											},
										}}
										data={loadedData}
									/>
								</Center>
							</Box>
							<Flex className="hidden-print" justifyContent="flex-end" alignItems="center" p={4}>
								<Tooltip label={t('Download Image')}>
									<IconButton
										size="sm"
										icon={<DownloadIcon w={3} h={3} />}
										onMouseDown={() => {
											const link = document.createElement('a');
											link.download = `graph-distribution-policy-gross-premium-by-channel.jpg`;
											link.href = chartRef?.current?.toBase64Image('image/jpeg', 1);
											link.click();
										}}
									/>
								</Tooltip>
							</Flex>
						</>
					)}
				</DataLoading>
			</Card>
		</Box>
	);
}

export default Graph;
